import {useCallback, useState} from 'react'
import {useLingui} from '@lingui/react/macro'

import {
  DOCUMENT_MAX_SIZE,
  isPdf,
  isSupportedDocumentMimeType,
  PDF_MAX_PAGES,
  SUPPORTED_DOCUMENT_MIME_TYPES,
  validateDocumentMeta,
} from '#/lib/document/limits'
import {countPdfPages} from '#/lib/document/pdf.web'
import {logger} from '#/logger'
import {atoms as a, useTheme} from '#/alf'
import {Button} from '#/components/Button'
import {PageText_Stroke2_Corner0_Rounded as PageTextIcon} from '#/components/icons/PageText'
import {Loader} from '#/components/Loader'
import {type DocumentFile} from '../state/document'

type Props = {
  onSelectDocument?: (file: DocumentFile) => void
  disabled?: boolean
  setError?: (error: string) => void
}

export function SelectDocumentBtn({
  onSelectDocument,
  disabled,
  setError,
}: Props) {
  const {t: l} = useLingui()
  const t = useTheme()
  const [isChecking, setIsChecking] = useState(false)

  const onPress = useCallback(async () => {
    if (!onSelectDocument) return
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = SUPPORTED_DOCUMENT_MIME_TYPES.join(',')
    input.style.display = 'none'
    document.body.appendChild(input)

    // Dismissing the picker fires no `change` event, so without this the input
    // and its listener would sit in the DOM for the rest of the session — one
    // more per cancelled attempt.
    input.addEventListener('cancel', () => input.remove())

    input.addEventListener('change', async () => {
      input.remove()
      if (!input.files || input.files.length === 0) return

      const file = input.files[0]

      if (!isSupportedDocumentMimeType(file.type)) {
        setError?.(l`Unsupported file type: ${file.type || file.name}`)
        return
      }

      const metaIssue = validateDocumentMeta({
        mimeType: file.type,
        size: file.size,
      })
      if (metaIssue === 'tooLarge') {
        setError?.(
          l`Documents must be under ${Math.round(
            DOCUMENT_MAX_SIZE / (1024 * 1024),
          )} MB`,
        )
        return
      }

      try {
        const bytes = new Uint8Array(await file.arrayBuffer())
        let pageCount: number | undefined

        if (isPdf(file.type)) {
          setIsChecking(true)
          try {
            pageCount = await countPdfPages(bytes)
            if (pageCount > PDF_MAX_PAGES) {
              setError?.(
                l`PDFs must be ${PDF_MAX_PAGES} pages or fewer (this one has ${pageCount})`,
              )
              return
            }
          } catch (e) {
            logger.error('Failed to count PDF pages', {safeMessage: e})
            setError?.(l`Could not read this PDF`)
            return
          } finally {
            setIsChecking(false)
          }
        }

        onSelectDocument({
          name: file.name,
          size: file.size,
          mimeType: file.type,
          uri: URL.createObjectURL(file),
          bytes,
          pageCount,
        })
      } catch (e) {
        logger.error('Failed to read document file', {safeMessage: e})
        setIsChecking(false)
        setError?.(l`Failed to read document file`)
      }
    })

    input.dispatchEvent(new MouseEvent('click'))
  }, [l, setError, onSelectDocument])

  return (
    <Button
      onPress={onPress}
      label={l`Attach document`}
      accessibilityHint={l`Opens document file picker`}
      style={a.p_sm}
      variant="ghost"
      shape="round"
      color="primary"
      disabled={disabled || isChecking}>
      {isChecking ? (
        <Loader size="sm" />
      ) : (
        <PageTextIcon size="lg" style={disabled && t.atoms.text_contrast_low} />
      )}
    </Button>
  )
}
